/**
 * Uma mensagem pronta enquanto é escrita: o título e o texto, ainda sem id.
 *
 * Mora na entity, e não na ação de criar, porque a correção de uma que já
 * existe escreve a mesma coisa, com os mesmos limites. Apagada a ação de
 * criar, a edição ainda precisaria disto.
 */

import {
  TEXTO_MAXIMO_DA_MENSAGEM_PRONTA,
  TITULO_MAXIMO_DA_MENSAGEM_PRONTA,
} from '@cliqcrm/contracts';

export interface RascunhoDeMensagemPronta {
  readonly titulo: string;
  readonly texto: string;
}

export const RASCUNHO_VAZIO: RascunhoDeMensagemPronta = { titulo: '', texto: '' };

/** Os limites do contrato: o `maxLength` dos campos sai de lá, e não de uma cópia. */
export const TITULO_MAXIMO = TITULO_MAXIMO_DA_MENSAGEM_PRONTA;
export const TEXTO_MAXIMO = TEXTO_MAXIMO_DA_MENSAGEM_PRONTA;

/** Título e texto com alguma coisa além de espaço: o que o contrato aceita. */
export function rascunhoCompleto(rascunho: RascunhoDeMensagemPronta): boolean {
  return rascunho.titulo.trim() !== '' && rascunho.texto.trim() !== '';
}

/*
 * Telefone (fixo ou celular, com ou sem DDD) e e-mail.
 *
 * Os dois podem ser da própria loja ("nosso WhatsApp é…"), e por isso a tela
 * AVISA e pede confirmação, sem recusar. CPF e placa, que nunca são legítimos
 * numa lista que a organização inteira lê, a API recusa (decisão de 03/11/2026,
 * docs/seguranca-lgpd.md §9).
 */
const TELEFONE = /(?<!\d)(?:\(?\d{2}\)?[\s-]?)?9?\d{4}[\s-]?\d{4}(?!\d)/g;
const EMAIL = /[^\s@]+@[^\s@]+\.[^\s@]+/g;

/** Os telefones (só os dígitos) e os e-mails (em minúsculas) do título e do texto. */
function contatos(rascunho: RascunhoDeMensagemPronta): Set<string> {
  const tudo = `${rascunho.titulo}\n${rascunho.texto}`;
  return new Set([
    ...[...tudo.matchAll(TELEFONE)].map((m) => m[0].replace(/\D/g, '')),
    ...[...tudo.matchAll(EMAIL)].map((m) => m[0].toLowerCase()),
  ]);
}

/**
 * O rascunho tem um telefone ou e-mail que `antes` não tinha.
 *
 * Só o NOVO pede confirmação: corrigir uma vírgula numa mensagem que já tinha o
 * telefone da loja não pode pedir a mesma confirmação toda vez. Ao criar,
 * `antes` é vazio e qualquer contato conta.
 */
export function temContatoNovo(
  rascunho: RascunhoDeMensagemPronta,
  antes: RascunhoDeMensagemPronta = RASCUNHO_VAZIO,
): boolean {
  const ja = contatos(antes);
  return [...contatos(rascunho)].some((contato) => !ja.has(contato));
}
